import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import { addNotification } from "notificationStore.js";
import { isValidDateForMonth } from "utils.js";

import {
  orderValidationsActions,
  getStockModalOrderValidations
} from "actions/orderValidationsActions.js";
import { getStockModalOrder } from "actions/orderActions.js";

import {
  validationsSelector,
  warningsSelector
} from "selectors/orderValidationsSelector.js";
import {
  orderInvoiceSelector,
  orderBuyplanSelector,
  isDaiwaBrandSelector,
  selectedShippingAddrIdSelector,
  orderBillingAddressSelector
} from "selectors/orderDetailsSelector.js";
import { orderDataSelector } from "selectors/orderSelector.js";
import { stockModelIdSelector } from "selectors/stockModelIdSelector.js";

import { haveValidationsChanged } from "reducers/orderValidationsReducer.js";

import CustomModal from "components/CustomModal/index.jsx";
import V2OrderDialogBody from "components/BuyplanReport/StockModal/V2/OrderDialogBody.jsx";
import V2OrderDialogFooter from "components/BuyplanReport/V2/OrderDialogFooter.jsx";
import { getUpdateStockModelSalesPerson } from "actions/updateSalesPersonActions";
import { setSalesPersonInOrderDetails } from "actions/orderDetailsActions";
import { setSalesPersonInBuyplanInfo } from "actions/buyplanInfoActions";
import i18next from "i18next";

import { getPayment } from "actions/stripePaymentActions.js";
import {
  stripePaymentsDataSelector,
  stripePaymentsErrorSelector,
  isLoadingSelector
} from "selectors/stripePaymentsSelector.js"
import { redirectToURL } from "apis/apiHelpers.js";
import Loader from 'components/Loader/Loader.jsx';
import customShippingAddressReducer, { getInitialState } from "reducers/customShippingAddressReducer";
import { updateForm } from "actions/customShippingAddressActions";

let submitOrderParams = null;
let payNowClicked = false;
let haveValidationsPassed = false;
let haveWarningsPassed = false;

/* Note: Always Sync V2 - src/components/BuyplanReport/StockModal/V2/OrderSummaryDialog.jsx */

const OrderSummaryDialog = ({ className, isOpen, closeModalCallback }) => {
  const dispatch = useDispatch();
  const stockModalId = useSelector(stockModelIdSelector);
  const validations = useSelector(validationsSelector);
  const warnings = useSelector(warningsSelector);
  const buyplan = useSelector(orderBuyplanSelector);
  const invoice = useSelector(orderInvoiceSelector);
  const orderData = useSelector(orderDataSelector);
  const orderNumber = orderData && orderData.get("order_number");
  const showShippingMethods = buyplan && buyplan.get("show_shipping_methods");
  const isDaiwaBrand = useSelector(isDaiwaBrandSelector);
  const billingAddress = useSelector(orderBillingAddressSelector);
  const selectedShippingAddrId = useSelector(selectedShippingAddrIdSelector);
  let shippingMethodLoclisationData = i18next.t("shippingMethod.required");
  let dateValidationLoclisationData = i18next.t("dateValidation.fail");
  let phoneNumberRequiredLoclisationData = i18next.t("address.form.errors.phoneNumberRequired");
  let stockModalOrderLoclisationData = i18next.t("stockModal.order.success", { orderNumber: orderNumber });

  const stripePaymentData = useSelector(stripePaymentsDataSelector);
  const errorMsgForToastr = useSelector(stripePaymentsErrorSelector);
  const shouldRender =  isOpen && (!stripePaymentData.get("redirect_url") && !orderNumber)
  const isStripePaymentLoading = useSelector(isLoadingSelector);
  const depositAmountVal = invoice && invoice.get("minimum_deposit_amount");

  const [custPurchaseOrder, setCustPurchaseOrder] = useState(null);
  const onPurchaseOrderBlurCallback = useCallback(evt => {
    setCustPurchaseOrder(evt.target.value);
  }, []);

  const [deliveryInstructions, setdeliveryInstructions] = useState(null);
  const onDeliveryInstructionsBlurCallback = useCallback(evt => {
    setdeliveryInstructions(evt.target.value);
  }, []);

  const [depositAmount, setdepositAmount] = useState(depositAmountVal);
  const onDepositAmountCallback = useCallback(evt => {
    setdepositAmount(evt.target.value);
  }, []); 

  const [netsuiteOrderType, setNetsuiteOrderType] = useState(null);
  const handleNetsuiteOrderTypeChange = selectedNetsuiteOrderType => {
    setNetsuiteOrderType(selectedNetsuiteOrderType.value);
  };

  const [shippingAddress, setShippingAddress] = useState(null);
  const handleShippingAddressChange = selectedShippingAddress => {
    setShippingAddress(selectedShippingAddress.value);
  };

  const [shippingMethod, setShippingMethod] = useState(null);
  const handleShippingMethodChange = selectedShippingMethod => {
    setShippingMethod(selectedShippingMethod.value);
  };

  const [deliveryDate, setDeliveryDate] = useState(null);
  const handleDeliveryDateChange = selectedDeliveryDate => {
    setDeliveryDate(selectedDeliveryDate);
  };

  const [cancelDate, setCancelDate] = useState(null);
  const handleCancelDateChange = selectedCancelDate => {
    setCancelDate(selectedCancelDate);
  };

  const [customShippingAddress, dispatchCustomShippingAddress] = useReducer(customShippingAddressReducer, getInitialState());

  const handleSalesPersonChange = selectedSalesPerson => {
    dispatch(
      getUpdateStockModelSalesPerson(stockModalId, selectedSalesPerson.value)
    );
    dispatch(
      setSalesPersonInOrderDetails({
        id: selectedSalesPerson.value,
        name: selectedSalesPerson.label
      })
    );
    dispatch(
      setSalesPersonInBuyplanInfo({
        id: selectedSalesPerson.value,
        name: selectedSalesPerson.label
      })
    );
  };

  const [deliveryDates, setDeliveryDates] = useState({});
  const handleDeliveryDatesChange = ({
    target: {
      name,
      value,
      dataset: { month }
    }
  }) => {
    const deliveryMonth = name.match(/[A-Z]{3}[\d]{2}/)?.[0];

    if (value && !isValidDateForMonth(value, month)) {
      addNotification({
        type: "danger",
        message: i18next.t("order.errors.incorrectDeliveryMonth", {
          value: value,
          deliveryMonth: deliveryMonth
        })
      });
    }

    const tmpDeliveryDates = { ...deliveryDates, [deliveryMonth]: value };
    setDeliveryDates(tmpDeliveryDates);
  };

  // eslint-disable-next-line no-eq-null
  if (stripePaymentData && stripePaymentData.get("redirect_url") != null)
  {
    redirectToURL(stripePaymentData.get("redirect_url"));
  }

  const handleCloseModal = useCallback(() => {
    submitOrderParams = null;
    payNowClicked = false;
    haveValidationsPassed = false;
    haveWarningsPassed = false;

    closeModalCallback();
  }, [closeModalCallback]);

  const submitOrderCallback = useCallback((payNow) => () => {
    payNowClicked = payNow;

    submitOrderParams = {
      stock_model: {
        company_id: buyplan.get("company_name"),
        retailer_id: buyplan.get("retailer_name"),
        id: stockModalId,
        buyplan_state: 'stock_model',
        order_detail_attributes: {
          order_number: null,
          customer_purchase_order: custPurchaseOrder,
          delivery_instructions: deliveryInstructions,
          deposit_amount: depositAmount,
          netsuite_order_type: netsuiteOrderType,
          shipping_address: shippingAddress,
          shipping_method: shippingMethod,
          delivery_days: deliveryDates,
          delivery_date: deliveryDate?.toDateString(),
          cancel_date: cancelDate?.toDateString(),
          total_price: invoice.get('total_price'),
          total_instant_pay_discount_price: invoice.get('total_instant_pay_discount_price'),
          postage_amount: invoice.get('total_postage'),
          total_tax_price_on_pay_now: invoice.get('total_tax_price_on_pay_now'),
          billing_address_id: billingAddress.get("id"),
          shipping_address_id: selectedShippingAddrId,
          custom_shipping_address: customShippingAddress.toJS()
        }
      }
    }

    const shouldValidateCustomAddressPhoneNumber = buyplan.get("allow_custom_shipping_address") &&
      buyplan.get("display_phone_number_field") &&
      !selectedShippingAddrId;
    const phoneNumber = (customShippingAddress.get("phone_number") || "").trim();
    if (shouldValidateCustomAddressPhoneNumber && !phoneNumber) {
      addNotification({
        type: "danger",
        message: phoneNumberRequiredLoclisationData
      });
      return;
    }

    if (showShippingMethods) {
      if (!shippingMethod) {
        addNotification({
          type: "danger",
          message: shippingMethodLoclisationData
        });
      } else {
        if (isDaiwaBrand) {
          if (cancelDate && deliveryDate > cancelDate) {
            addNotification({
              type: "danger",
              message: dateValidationLoclisationData
            });
          } else {
            dispatch(getStockModalOrderValidations(stockModalId, submitOrderParams));
          }
        } else {
          dispatch(getStockModalOrderValidations(stockModalId, submitOrderParams));
        }
      }
    } else {
      dispatch(getStockModalOrderValidations(stockModalId, submitOrderParams));
    }
  }, [
    dispatch,
    stockModalId,
    showShippingMethods,
    shippingMethod,
    cancelDate,
    deliveryDate,
    isDaiwaBrand,
    buyplan,
    custPurchaseOrder,
    deliveryInstructions,
    depositAmount,
    netsuiteOrderType,
    shippingAddress,
    deliveryDates,
    invoice,
    dateValidationLoclisationData,
    shippingMethodLoclisationData,
    billingAddress,
    selectedShippingAddrId,
    customShippingAddress,
    phoneNumberRequiredLoclisationData
  ]);

  useEffect(() => {
    if (haveValidationsChanged(validations)) {
      const failingValidations = validations.filter(validation => {
        return validation.get("status") !== "pass";
      });

      if (failingValidations.size === 0) {
        haveValidationsPassed = true;
      } else {
        failingValidations.forEach(validation => {
          addNotification({
            type: "error",
            message: validation.get("name")
          });
        });
      }
    }

    if (!warnings || warnings.size === 0) {
      haveWarningsPassed = true;
    } else {
      warnings.forEach(warning => {
        addNotification({
          type: "warning",
          message: warning
        });
      });
    }

    if (haveValidationsPassed && haveWarningsPassed && !payNowClicked && !orderNumber) {
      dispatch(
        getStockModalOrder(stockModalId, submitOrderParams)
      );
    }

    if (haveValidationsPassed && haveWarningsPassed && payNowClicked && !errorMsgForToastr) {
      if (!stripePaymentData.get("redirect_url") && !isStripePaymentLoading) {
        dispatch(getPayment(submitOrderParams));
      }
    }

    return () => dispatch({ type: orderValidationsActions.reset });
  }, [
    validations,
    warnings,
    dispatch,
    stockModalId,
    orderNumber,
    errorMsgForToastr,
    isStripePaymentLoading,
    stripePaymentData
  ]);

  useEffect(() => {
    if (orderNumber) {
      addNotification({
        message: stockModalOrderLoclisationData
      });
    }
  }, [orderNumber, stockModalOrderLoclisationData]);

  if (payNowClicked && haveValidationsPassed && haveWarningsPassed && isStripePaymentLoading) {
    return (
      <CustomModal
          className={className}
          isOpen={isOpen}
          title={i18next.t("order.paymentDetails")}
          body={
            <Loader/>
          }
          closeModalCallback={handleCloseModal}
      />)
  }

  return (
    shouldRender && (
      <CustomModal
        className={className}
        isOpen={isOpen}
        title={i18next.t("order.orderDetails")}
        body={ shouldRender &&
          <V2OrderDialogBody
            onPurchaseOrderBlurCallback={onPurchaseOrderBlurCallback}
            onDeliveryInstructionsBlurCallback={
              onDeliveryInstructionsBlurCallback
            }
            onDepositAmountCallback={onDepositAmountCallback}
            handleNetsuiteOrderTypeChange={handleNetsuiteOrderTypeChange}
            handleShippingAddressChange={handleShippingAddressChange}
            handleShippingMethodChange={handleShippingMethodChange}
            handleDeliveryDatesChange={handleDeliveryDatesChange}
            handleDeliveryDateChange={handleDeliveryDateChange}
            handleCancelDateChange={handleCancelDateChange}
            handleSalesPersonChange={handleSalesPersonChange}
            deliveryDate={deliveryDate}
            cancelDate={cancelDate}
            onCustomShippingAddressChangeCallback={(evt) => dispatchCustomShippingAddress(updateForm(evt))}
          />
        }
        footer={
          <V2OrderDialogFooter
            closeModalFn={handleCloseModal}
            onClickCallback={submitOrderCallback}
          />
        }
        closeModalCallback={handleCloseModal}
      />
    )
  );
};

const StyledOrderSummaryDialog = styled(OrderSummaryDialog)`
  min-width: 90%;

  @media (max-width: 575.98px) {
    padding-top: 60px;
  }

  .modal-header {
    align-items: center;
  }

  .modal-title {
    font-size: 24.5px;
    line-height: 30px;
    font-weight: 700;
  }

  .device-info {
    display: flex;
    flex-wrap: wrap;

    @media (max-width: 575.98px) {
      .invoice-from,
      .invoice-to {
        width: 100%;
      }
    }
  }

  .invoice-to {
    margin: 0 0 0 50px;

    @media (max-width: 575.98px) {
      margin: 0;
    }
  }

  .modal-footer {
    .btn {
      font-size: 14px;
      line-height: 18px;
      border-radius: 0;
      padding: 5px 9px;

      &.btn-success {
        background-color: #40bf40;
        border-color: #40bf40;
      }
    }
  }
`;

export default React.memo(StyledOrderSummaryDialog);
